import axios, { type AxiosError, type GenericAbortSignal } from 'axios';
import dayjs from 'dayjs';

import type {
  TClientApi,
  TClientDetailedApi
} from '@/components/App/Clients/types';
import { ROUTES } from '@/components/Shared/routes/routes';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { getNavigate } from '@/services/helpers/navigate-helper';
import { getUserToken, setUserToken } from '@/services/user-hash';
import { setIsAuthErrorModal } from '@/store/auth.slice';
import type { SystemNotificationResponse } from '@/types/notifications';
import type { TModule, TUserResponse } from '@/types/user';

import { encrypt } from '../services/encrypt';
import { store } from '../store';
import { createIndexApi, indexApi } from './index.api';

type CustomAxiosError = AxiosError<{
  message?: string | string[];
  statusCode: number;
}>;

export class ApiConnector {
  private token: string | undefined;
  private refresh: string | undefined;

  async call<Request, Response>({
    path,
    method = 'get',
    body,
    params,
    abortSignal,
    headers,
    baseUrl,
    cancelable = false
  }: {
    path: string;
    method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
    body?: Request;
    params?: Request;
    abortSignal?: GenericAbortSignal | undefined;
    headers?: Record<string, string>;
    baseUrl?: string;
    cancelable?: boolean;
  }): Promise<Response> {
    const clonedParams = structuredClone(params);
    for (const param in clonedParams) {
      if (clonedParams[param] === undefined || !String(clonedParams[param]))
        delete clonedParams[param];
    }

    if (!cancelable && !abortSignal) {
      abortSignal = new AbortController().signal;
    }

    try {
      const api = baseUrl ? createIndexApi(baseUrl) : indexApi;
      const { data } = await api(path, {
        method,
        data: body,
        params: clonedParams,
        headers,
        signal: abortSignal
      });
      return data;
    } catch (e) {
      const error = e as CustomAxiosError;

      const statusCode = error?.response?.data?.statusCode || error.status;
      let message: string = 'Произошла ошибка! Попробуйте позже';
      const messages: string[] = [];

      if (statusCode && statusCode < 500) {
        if (Array.isArray(error?.response?.data?.message)) {
          for (const item of error.response.data.message) {
            if (typeof item === 'string') {
              messages.push(item);
            }
          }
        } else {
          message = error?.response?.data?.message || message;
        }
      }

      const err = new Error(message);
      err.messages = messages;
      err.statusCode = statusCode;
      err.isCancelled = axios.isCancel(e);
      throw err;
    }
  }

  public async isValid(): Promise<boolean> {
    try {
      const { accessToken, emailAllur } = getUserToken();
      const res = await indexApi({
        url: `${process.env.REACT_APP_AUTH_URL}/auth/validate`,
        method: 'post',
        withCredentials: false,
        signal: new AbortController().signal,
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${accessToken}`
        },
        data: {
          email: emailAllur,
          accessToken
        }
      });
      return res.data.success;
    } catch {
      return false;
    }
  }

  public timeForCheck() {
    try {
      const navigate = getNavigate();
      const loginTime = localStorage.getItem('loginTime');
      if (!loginTime) {
        clearLocalStorageHelper();
        navigate(ROUTES.login);
        return;
      }

      const now = new Date().getTime();
      if (now > Number(loginTime)) {
        store.dispatch(setIsAuthErrorModal('reauthenticate'));
      }
    } catch {
      throw new Error('Refresh Token Expired! Logging out');
    }
  }

  public async authJwt(
    email: string,
    password: string,
    isRemember: boolean,
    ip: string,
    lat: string,
    long: string
  ): Promise<any> {
    try {
      const encData = encrypt(
        JSON.stringify({
          login: email,
          password: password,
          ip,
          lat,
          long
        })
      );
      const res = await axios({
        url: `${process.env.REACT_APP_AUTH_URL}/auth/login`,
        method: 'post',
        withCredentials: false,
        headers: {
          'Access-Control-Allow-Origin': '*'
        },
        data: { data: encData }
      });
      const accessToken = res.data?.accessToken;
      const refreshToken = res.data?.refreshToken;
      this.token = accessToken;
      this.refresh = refreshToken;

      if (accessToken) {
        setUserToken({ accessToken, refreshToken, emailAllur: email });
        localStorage.setItem('isAuthenticated', 'true');
        const loginTime = isRemember
          ? dayjs().add(1, 'days').toDate().getTime()
          : dayjs().add(1, 'hours').toDate().getTime();
        localStorage.setItem('loginTime', String(loginTime));
      }
      return {
        status: !!accessToken,
        message: accessToken ? '' : res.data
      };
    } catch (e) {
      const error = e as CustomAxiosError;
      throw error.response?.data;
    }
  }

  public async forgotePassword(email: string): Promise<any> {
    try {
      const res = await axios({
        url: `${process.env.REACT_APP_AUTH_URL}/auth/forgot_password`,
        method: 'post',
        withCredentials: false,
        headers: {
          'Access-Control-Allow-Origin': '*'
        },
        data: {
          email: email
        }
      });

      return res;
    } catch (error) {
      throw new Error(String(error), { cause: error });
    }
  }

  public async resetPassword(password: string, token: string): Promise<any> {
    try {
      const res = await axios({
        url: `${process.env.REACT_APP_AUTH_URL}/auth/reset_password`,
        method: 'post',
        withCredentials: false,
        headers: {
          'Access-Control-Allow-Origin': '*'
        },
        data: {
          password: password,
          token: token
        }
      });

      return res;
    } catch (error) {
      throw new Error(String(error), { cause: error });
    }
  }

  public async logout(): Promise<void> {
    try {
      await axios.post(
        `${process.env.REACT_APP_AUTH_URL}/auth/logout`,
        {},
        {
          headers: {
            Authorization: `Bearer ${this.token}`,
            'Refresh-Token': this.refresh!
          }
        }
      );
    } catch (error) {
      throw new Error(String(error), { cause: error });
    } finally {
      this.token = undefined;
      this.refresh = undefined;
    }
  }

  getUserAccount(): Promise<TUserResponse> {
    return this.call({ path: 'users/userAccount', method: 'get' });
  }

  getUserMenu(dealerId: string): Promise<TModule[]> {
    return this.call({
      path: `users/modules?dealerId=${dealerId}`,
      method: 'get'
    });
  }

  getClientByIin(
    iin: string,
    abortSignal?: AbortSignal
  ): Promise<TClientApi | null> {
    return this.call({ path: `clients/iin/${iin}`, abortSignal });
  }

  getClientById(id: string): Promise<TClientDetailedApi> {
    return this.call({ path: `clients/${id}` });
  }

  startNotification(): Promise<{ payload: SystemNotificationResponse[] }> {
    return this.call({
      path: 'admin/notifications/start/all'
    });
  }
}

export const apiConnector = new ApiConnector();
